/**
 * HẸN GIỜ CÔNG KHAI VIDEO (06/10/2026) — phần tính toán thuần của màn «Đăng video»: viết giờ hẹn theo giờ
 * Việt Nam, đếm video theo trạng thái, thời lượng.
 */
import {
  type LiveBroadcastDto,
  AI_PLAYLIST_BRIEF_MAX,
  AI_PLAYLIST_TITLES_MAX,
  AI_VIDEO_BRIEF_MAX,
  AI_VIDEO_TITLES_MAX,
  DEFAULT_UPLOAD_PRIVACY,
  PLAYLIST_DESCRIPTION_MAX,
  PLAYLIST_TITLE_MAX,
  YOUTUBE_PRIVACY_LABELS,
  vietnamParts,
  type AiPlaylistSuggestRequest,
  type AiVideoKind,
  type AiVideoSeoRequest,
  type VideoMetadata,
  type VideoVisibilityChoice,
  type YoutubePrivacy,
  type YoutubePublishTemplateDto,
  type YoutubeUnpublishedVideoDto,
} from '@halo/shared';
import { normalizeForSearch } from './format';

/** «05:00 07/10/2026» — giờ Việt Nam của một giờ hẹn, dù máy người xem đặt múi giờ nào. */
export function formatPublishAt(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const { day, time } = vietnamParts(at);
  const [year, month, date] = day.split('-');
  return `${time} ${date}/${month}/${year}`;
}

/** «12:30» / «1:02:03». */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, '0');
  return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}` : `${minutes}:${rest}`;
}

/** Nhãn trạng thái một video: «Hẹn 05:00 07/10/2026», «Công khai», «Riêng tư», «Không công khai». */
export function videoStateLabel(
  video: Pick<YoutubeUnpublishedVideoDto, 'privacy' | 'publishAt'>,
): string {
  return video.publishAt
    ? `Hẹn ${formatPublishAt(video.publishAt)}`
    : YOUTUBE_PRIVACY_LABELS[video.privacy];
}

/** Bộ lọc trạng thái của bảng Video (06/10/2026: «giao diện kênh những video công khai, không công khai…»). */
export type VideoFilter = 'all' | 'public' | 'unlisted' | 'private' | 'scheduled';

export const VIDEO_FILTERS: readonly { value: VideoFilter; label: string }[] = [
  { value: 'all', label: 'Tất cả' },
  { value: 'public', label: 'Công khai' },
  { value: 'unlisted', label: 'Không công khai' },
  { value: 'private', label: 'Riêng tư' },
  { value: 'scheduled', label: 'Đã hẹn' },
];

/** Video đã hẹn giờ tính là «đã hẹn», không tính vào «riêng tư» dù YouTube đang để riêng tư chờ giờ. */
export function matchesVideoFilter(
  video: YoutubeUnpublishedVideoDto,
  filter: VideoFilter,
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'scheduled':
      return Boolean(video.publishAt);
    default:
      return !video.publishAt && video.privacy === filter;
  }
}

/** «40 công khai · 2 đã hẹn · 3 riêng tư · 1 không công khai» — dòng tóm tắt đầu bảng. */
export function publishSummary(videos: readonly YoutubeUnpublishedVideoDto[]): string {
  const count = (filter: VideoFilter) =>
    videos.filter((video) => matchesVideoFilter(video, filter)).length;
  const scheduled = count('scheduled');
  return [
    count('public') ? `${count('public')} công khai` : '',
    scheduled ? `${scheduled} video đã hẹn` : '',
    count('private') ? `${count('private')} riêng tư` : '',
    count('unlisted') ? `${count('unlisted')} không công khai` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * TÊN KÊNH LUÔN LÀ TÊN HIỆN TẠI (06/10/2026, chủ dự án: «tên kênh mỗi lần tải lại bạn update đúng tên hiện tại»).
 * Máy chủ đọc tên hiện tại của kênh từ YouTube mỗi lần đọc danh sách video / thông tin kênh và ghi vào dòng nối;
 * ô chọn kênh và phần Kết nối lại đọc từ dòng nối. Tên vừa đọc (`freshTitle`) khác tên đang hiện (`knownTitle`)
 * thì trả về dấu mốc để tải lại danh sách nối; đã tải lại cho đúng dấu mốc ấy (`lastMark`) thì thôi — máy chủ có
 * trả tên lệch mãi cũng không lặp vô tận. Không có kênh, chưa có tên, hoặc kênh chưa nằm trong danh sách nối → null.
 */
export function connectRefreshMark(
  youtubeId: string,
  freshTitle: string | undefined,
  knownTitle: string | undefined,
  lastMark: string,
): string | null {
  if (!youtubeId || !freshTitle) return null;
  if (knownTitle === undefined || knownTitle === freshTitle) return null;
  const mark = youtubeId + ':' + freshTitle;
  return mark === lastMark ? null : mark;
}

/**
 * Tên video gần đây gửi cho AI khi viết mô tả kênh: bỏ trống, bỏ trùng, tối đa `max` bài (danh sách của máy
 * chủ đã xếp mới nhất trước).
 */
export function recentVideoTitles(videos: readonly { title: string }[], max: number): string[] {
  const titles: string[] = [];
  const seen = new Set<string>();
  for (const video of videos) {
    const title = video.title.trim().slice(0, 200);
    if (!title || seen.has(title)) continue;
    seen.add(title);
    titles.push(title);
    if (titles.length >= max) break;
  }
  return titles;
}

/*
 * NHÓM KÊNH (06/10/2026, chủ dự án: «Tôi muốn chia nhóm kênh, bạn thiết kế từng file nhóm kênh tôi dễ quản lý»):
 * ô lọc nhóm + ô chọn kênh hiện theo nhóm như thư mục.
 */

/** Mã «nhóm» ảo cho kênh chưa xếp nhóm trong ô lọc. */
export const UNGROUPED_GROUP = '~none';

export function channelsInGroup<T extends { groupId: string | null }>(
  channels: readonly T[],
  groupId: string,
): T[] {
  if (!groupId) return [...channels];
  if (groupId === UNGROUPED_GROUP) return channels.filter((channel) => !channel.groupId);
  return channels.filter((channel) => channel.groupId === groupId);
}

export interface ChannelOption {
  value: string;
  label: string;
}
export interface ChannelOptionGroup {
  label: string;
  options: ChannelOption[];
}

/** Lựa chọn cho ô chọn kênh: theo nhóm (kênh không nhóm xuống cuối); chưa có nhóm nào thì danh sách phẳng. */
export function channelOptionGroups(
  channels: readonly { youtubeId: string; title: string; groupId: string | null }[],
  groups: readonly { id: string; name: string }[],
): (ChannelOption | ChannelOptionGroup)[] {
  const option = (channel: { youtubeId: string; title: string }): ChannelOption => ({
    value: channel.youtubeId,
    label: channel.title || channel.youtubeId,
  });
  if (groups.length === 0) return channels.map(option);
  const result: (ChannelOption | ChannelOptionGroup)[] = [];
  for (const group of groups) {
    const inside = channels.filter((channel) => channel.groupId === group.id);
    if (inside.length > 0) result.push({ label: group.name, options: inside.map(option) });
  }
  const loose = channels.filter(
    (channel) => !channel.groupId || !groups.some((group) => group.id === channel.groupId),
  );
  if (loose.length > 0) result.push({ label: 'Chưa xếp nhóm', options: loose.map(option) });
  return result;
}

/**
 * Chế độ của thanh công cụ Tải lên (06/10/2026, chủ dự án: «cài đặt mặc định tải lên dạng không công khai»):
 * không công khai (mặc định) · hẹn công khai (riêng tư tới giờ hẹn) · để riêng tư.
 */
export type UploadMode = 'unlisted' | 'schedule' | 'private';
export const UPLOAD_MODES: readonly { value: UploadMode; label: string }[] = [
  { value: 'unlisted', label: 'Không công khai' },
  { value: 'schedule', label: 'Hẹn công khai' },
  { value: 'private', label: 'Để riêng tư' },
];
export const DEFAULT_UPLOAD_MODE: UploadMode = DEFAULT_UPLOAD_PRIVACY;

/** Hiển thị gửi lên YouTube lúc mở phiên: hẹn giờ thì PHẢI riêng tư (YouTube tự công khai đúng giờ). */
export const uploadPrivacyOf = (mode: UploadMode): YoutubePrivacy =>
  mode === 'schedule' ? 'private' : mode;

/**
 * Gói dữ liệu gửi «Viết bằng AI» cho một video / buổi phát (06/10/2026): những gì đang có, mẫu SEO của kênh,
 * tên video gần đây (cùng truy vấn với phần Video) và gợi ý thêm — cắt về trần của hợp đồng.
 */
export function videoSeoRequest(input: {
  kind: AiVideoKind;
  channelTitle: string;
  meta: VideoMetadata;
  template: Pick<YoutubePublishTemplateDto, 'title' | 'description' | 'tags'> | undefined;
  videos: readonly { title: string }[];
  brief: string;
}): AiVideoSeoRequest {
  return {
    kind: input.kind,
    channelTitle: input.channelTitle.trim().slice(0, 200),
    title: input.meta.title.trim().slice(0, 300),
    description: input.meta.description.slice(0, 6000),
    tags: input.meta.tags.slice(0, 200).map((tag) => tag.slice(0, 100)),
    template: {
      title: (input.template?.title ?? '').slice(0, 300),
      description: (input.template?.description ?? '').slice(0, 6000),
      tags: (input.template?.tags ?? '').slice(0, 2000),
    },
    titles: recentVideoTitles(input.videos, AI_VIDEO_TITLES_MAX),
    brief: input.brief.trim().slice(0, AI_VIDEO_BRIEF_MAX),
  };
}

/**
 * «GIỜ MỞ» của một video (06/10/2026, chủ dự án: «cập nhật thêm giờ mở, danh sách phát, view, like, comment»):
 * đã hẹn → giờ hẹn; đã công khai → `publishedAt` của YouTube (với video từng riêng tư, YouTube ghi đó là lúc
 * công khai, không phải lúc tải lên); còn lại → giờ tải lên (chưa mở, chưa hẹn).
 */
export type VideoOpenKind = 'scheduled' | 'opened' | 'uploaded';
export const OPEN_TIME_LABELS: Record<VideoOpenKind, string> = {
  scheduled: 'hẹn mở',
  opened: 'đã mở',
  uploaded: 'tải lên',
};
export function videoOpenTime(
  video: Pick<YoutubeUnpublishedVideoDto, 'publishAt' | 'privacy' | 'uploadedAt'>,
): { at: string | null; kind: VideoOpenKind } {
  if (video.publishAt) return { at: video.publishAt, kind: 'scheduled' };
  if (video.privacy === 'public') return { at: video.uploadedAt, kind: 'opened' };
  return { at: video.uploadedAt, kind: 'uploaded' };
}

/** Tên các danh sách phát chứa một video, theo thứ tự danh sách của kênh. */
export function playlistTitlesOf(
  videoId: string,
  playlists: readonly { title: string; videoIds: readonly string[] }[],
): string[] {
  return playlists
    .filter((playlist) => playlist.videoIds.includes(videoId))
    .map((playlist) => playlist.title);
}

/**
 * HỘP SỬA VIDEO TRỌN GÓI (07/10/2026, chủ dự án: «chỉnh sửa hiển thị … · tích chọn, bỏ các playlist có sẵn trong
 * kênh · dùng AI tạo thêm danh sách phát») — luật thuần của hộp.
 */

/** Vì sao không chọn được hiển thị này với trạng thái hiện tại — cùng luật `actionProblem` của máy chủ. */
export function visibilityChoiceProblem(
  video: Pick<YoutubeUnpublishedVideoDto, 'privacy' | 'publishAt' | 'problem'>,
  choice: VideoVisibilityChoice,
): string {
  if (choice === 'keep') return '';
  if (video.problem) return video.problem;
  const isPublic = video.privacy === 'public';
  switch (choice) {
    case 'schedule':
    case 'public':
      return isPublic ? 'Video đã công khai' : '';
    case 'unlisted':
      return video.privacy === 'unlisted' && !video.publishAt ? 'Video đã là không công khai' : '';
    default:
      return video.privacy === 'private' && !video.publishAt ? 'Video đã riêng tư' : '';
  }
}

/** Mã các danh sách phát đang chứa video (từ dữ liệu cột «Danh sách phát»). */
export function playlistIdsOf(
  videoId: string,
  playlists: readonly { id: string; videoIds: readonly string[] }[],
): string[] {
  return playlists
    .filter((playlist) => playlist.videoIds.includes(videoId))
    .map((playlist) => playlist.id);
}

/** Khác biệt giữa danh sách đã tích lúc mở hộp và lúc ghi: thêm vào đâu, bỏ khỏi đâu. */
export function playlistChanges(
  initial: readonly string[],
  ticked: readonly string[],
): { add: string[]; remove: string[] } {
  const before = new Set(initial);
  const after = new Set(ticked);
  return {
    add: [...after].filter((id) => !before.has(id)),
    remove: [...before].filter((id) => !after.has(id)),
  };
}

/** Gói dữ liệu gửi «Gợi ý danh sách phát bằng AI»: video đang sửa, danh sách đang có, tên video gần đây. */
export function playlistSuggestRequest(input: {
  channelTitle: string;
  meta: VideoMetadata;
  playlists: readonly { title: string }[];
  videos: readonly { title: string }[];
  brief: string;
}): AiPlaylistSuggestRequest {
  return {
    channelTitle: input.channelTitle.trim().slice(0, 200),
    video: {
      title: input.meta.title.trim().slice(0, 300),
      description: input.meta.description.slice(0, 6000),
      tags: input.meta.tags.slice(0, 200).map((tag) => tag.slice(0, 100)),
    },
    playlists: input.playlists
      .map((playlist) => playlist.title.trim().slice(0, 200))
      .filter(Boolean)
      .slice(0, AI_PLAYLIST_TITLES_MAX),
    titles: recentVideoTitles(input.videos, AI_VIDEO_TITLES_MAX),
    brief: input.brief.trim().slice(0, AI_PLAYLIST_BRIEF_MAX),
  };
}

/*
 * LỌC KÊNH ĐÃ NỐI (07/10/2026, chủ dự án: «cho tôi chức năng lọc nhóm, network, kênh»): theo nhóm, theo network
 * tự nhập và theo tên / mã kênh (gõ không dấu cũng ra) — dùng ở bảng Kết nối và thanh chọn kênh đầu trang.
 */

/** Giá trị lọc «kênh chưa ghi network». */
export const NO_NETWORK = '~none';

/** Lựa chọn cho ô lọc network: mọi network, từng network đang có (kèm số kênh), chưa ghi (nếu có). */
export function networkOptions(
  channels: readonly { network: string }[],
): { value: string; label: string }[] {
  const counts = new Map<string, number>();
  let blank = 0;
  for (const channel of channels) {
    const network = channel.network.trim();
    if (!network) blank += 1;
    else counts.set(network, (counts.get(network) ?? 0) + 1);
  }
  const options = [
    { value: '', label: 'Mọi network' },
    ...[...counts.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'vi'))
      .map(([network, count]) => ({ value: network, label: `${network} (${count})` })),
  ];
  if (blank > 0) options.push({ value: NO_NETWORK, label: `Chưa ghi network (${blank})` });
  return options;
}

export interface ConnectionFilter {
  /** Tên hay mã kênh, so không dấu, không phân biệt hoa thường; rỗng = tất cả. */
  query?: string;
  /** '' = mọi nhóm; `UNGROUPED_GROUP` = chưa xếp nhóm. */
  groupId?: string;
  /** '' = mọi network; `NO_NETWORK` = chưa ghi. */
  network?: string;
  /** '' = mọi dự án; `NO_PROJECT` = chưa thuộc dự án nào; còn lại = mã client (08/10/2026). */
  project?: string;
}

/** Giá trị ô lọc dự án cho kênh chưa thuộc dự án nào (chưa nối client nào, chưa giao). */
export const NO_PROJECT = '~none';

/** Kênh thuộc dự án nào: dự án chính, không có thì các client đã nối. */
function projectsOf(channel: {
  homeClientKey?: string | null;
  clientKeys?: readonly string[];
}): string[] {
  return channel.homeClientKey ? [channel.homeClientKey] : [...(channel.clientKeys ?? [])];
}

/** Ô lọc dự án: từng dự án kèm số kênh (theo thứ tự thêm dự án), thêm «chưa thuộc dự án» khi có. */
export function projectOptions(
  channels: readonly { homeClientKey?: string | null; clientKeys?: readonly string[] }[],
  clients: readonly { key: string; name: string }[],
): { value: string; label: string }[] {
  const none = channels.filter((channel) => !projectsOf(channel).length).length;
  return [
    { value: '', label: 'Mọi dự án' },
    ...clients.map((client) => ({
      value: client.key,
      label: `${client.name} (${channels.filter((channel) => projectsOf(channel).includes(client.key)).length})`,
    })),
    ...(none ? [{ value: NO_PROJECT, label: `Chưa thuộc dự án (${none})` }] : []),
  ];
}

export function filterConnections<
  T extends {
    youtubeId: string;
    title: string;
    groupId: string | null;
    network: string;
    homeClientKey?: string | null;
    clientKeys?: readonly string[];
  },
>(channels: readonly T[], filter: ConnectionFilter): T[] {
  const needle = normalizeForSearch(filter.query ?? '');
  const network = filter.network ?? '';
  const project = filter.project ?? '';
  return channelsInGroup(channels, filter.groupId ?? '').filter((channel) => {
    const own = channel.network.trim();
    if (network === NO_NETWORK ? own !== '' : network && own !== network) return false;
    const projects = projectsOf(channel);
    if (project === NO_PROJECT ? projects.length > 0 : project && !projects.includes(project)) {
      return false;
    }
    return !needle || normalizeForSearch(`${channel.title} ${channel.youtubeId}`).includes(needle);
  });
}

// ---------------------------------------------------------------- tạo nhiều danh sách phát

export interface PlaylistLine {
  /** Dòng gốc đã cắt khoảng trắng — để báo đúng dòng bị bỏ qua. */
  raw: string;
  title: string;
  description: string;
  /** Vì sao bỏ qua; null = sẽ tạo. */
  problem: string | null;
}

/** Số thứ tự («1.», «10)», «01 -», «3:») hay gạch đầu dòng ở đầu dòng — người ta hay đánh số trong file txt. */
const PLAYLIST_LINE_PREFIX = /^(?:\d{1,3}\s*[.)\-:]|[-•*–—+])\s*/;

/**
 * Bóc danh sách phát từ văn bản dán vào (07/10/2026, chủ dự án: «Tôi có 10 danh sách phát ở file txt»): mỗi dòng
 * một danh sách; phần sau dấu `|` hoặc Tab đầu tiên là mô tả; dòng trống bỏ qua. Dòng bị BỎ QUA kèm lý do: thiếu
 * tên, tên quá dài, trùng dòng trên, hoặc kênh ĐÃ CÓ danh sách cùng tên (so không dấu, không phân biệt hoa thường —
 * tạo thêm một «Nhạc Vàng» nữa chỉ làm kênh rối). Thứ tự giữ nguyên như trong file.
 */
export function parsePlaylistLines(
  text: string,
  existing: readonly { title: string }[],
): PlaylistLine[] {
  const taken = new Set(existing.map((item) => normalizeForSearch(item.title)));
  const seen = new Set<string>();
  const lines: PlaylistLine[] = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const raw = rawLine.trim();
    if (!raw) continue;
    const body = raw.replace(PLAYLIST_LINE_PREFIX, '').trim();
    const cut = body.search(/[\t|]/);
    const title = (cut === -1 ? body : body.slice(0, cut)).trim();
    const description = (cut === -1 ? '' : body.slice(cut + 1))
      .replace(/^[\s|]+/, '')
      .trim()
      .slice(0, PLAYLIST_DESCRIPTION_MAX);
    const key = normalizeForSearch(title);
    let problem: string | null = null;
    if (!title) problem = 'Thiếu tên';
    else if (title.length > PLAYLIST_TITLE_MAX) problem = `Tên quá ${PLAYLIST_TITLE_MAX} ký tự`;
    else if (seen.has(key)) problem = 'Trùng dòng trên';
    else if (taken.has(key)) problem = 'Kênh đã có danh sách này';
    if (problem === null) seen.add(key);
    lines.push({ raw, title, description, problem });
  }
  return lines;
}

// ---------------------------------------------------------------- xoay vòng client (08/10/2026)

/** Phần trăm hạn mức đã dùng để vẽ thanh: Google đã báo hết thì 100 dù số tự đếm còn thấp hơn. */
export function quotaPercent(used: number, limit: number, exhausted: boolean): number {
  if (exhausted) return 100;
  if (!(limit > 0)) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

/** Câu máy chủ trả khi mọi client đều hết hạn mức — gặp câu này thì dừng cả lô, làm tiếp cũng hỏng. */
export function isQuotaExhaustedMessage(message: string): boolean {
  return message.startsWith('Hết hạn mức YouTube API');
}

// ---------------------------------------------------------------- claim của video (08/10/2026)

export interface RightsTag {
  label: string;
  tone: 'success' | 'warning' | 'error' | 'default';
  hint: string;
}

/**
 * Nhãn claim cạnh link video (đổi 08/10/2026, chủ dự án: «Video không dính claim BQ bạn để trống · video dính claim BQ
 * bạn note là Claim BQ»): một nhãn «Claim BQ» khi YouTube cho thấy dấu hiệu claim bản quyền — video bị chặn / chỉ cho
 * xem ở vài nước, hay bị từ chối vì claim / bản quyền; không thì không nhãn nào. Claim chỉ hạn chế thu nhập (Studio ghi
 * «Có thể bị hạn chế thu nhập») YouTube Data API không trả, nên không hiện được.
 */
export function rightsTags(
  rights:
    | {
        licensed: boolean;
        blockedCountries: number;
        allowedCountries: number | null;
        rejection: string;
      }
    | undefined,
  rejectionLabels: Record<string, string>,
): RightsTag[] {
  if (!rights) return [];
  const reasons: string[] = [];
  if (rights.rejection) {
    reasons.push(`YouTube từ chối video: ${rejectionLabels[rights.rejection] ?? rights.rejection}`);
  }
  if (rights.blockedCountries > 0) reasons.push(`bị chặn xem ở ${rights.blockedCountries} nước`);
  if (rights.allowedCountries !== null)
    reasons.push(`chỉ xem được ở ${rights.allowedCountries} nước`);
  if (!reasons.length) return [];
  return [{ label: 'Claim BQ', tone: 'error', hint: reasons.join(' · ') }];
}

// ---------------------------------------------------------------- ảnh bìa: phá bộ đệm trình duyệt (17–18/10/2026)

/**
 * ẢNH BÌA TRÊN BẢNG VIDEO KHÔNG ĐỔI (17/10/2026 «tôi update thum nhưng không cập nhật»; 18/10/2026 «trên web vẫn chưa
 * hiện thum»): YouTube giữ NGUYÊN địa chỉ ảnh (i.ytimg.com/vi/<id>/mqdefault.jpg) khi ảnh đổi — lúc mới tải lên là ảnh
 * xám «…», xử lý xong hay thay ảnh bìa mới là ảnh thật — nên thẻ `<img>` đã nạp địa chỉ ấy thì trình duyệt không hỏi
 * lại, tab mở cả ngày cứ thấy ảnh cũ. Cách làm: địa chỉ ảnh luôn mang `halo=<mốc>`, mốc đổi là trình duyệt xin ảnh mới
 * (KHÔNG dùng tên `v`: i.ytimg.com coi `v=` là phiên bản ảnh, giá trị lạ thì trả 404 kèm ảnh xám — bản `0d0dbcf`/`040e473`
 * 17/10/2026 dùng `v=` nên chính nó làm ảnh bìa xám; `halo=` / `_=` / `x=` thì máy chủ ảnh bỏ qua):
 *   • video vừa thay ảnh trong app → mốc = lúc thay (chính xác, có ngay);
 *   • bấm «Tải lại» → mốc = lúc bấm, cho MỌI video (người bấm muốn thấy số liệu + ảnh mới);
 *   • còn lại → giờ chẵn của lần tải danh sách gần nhất: tab mở lâu tự làm mới ảnh mỗi giờ, trong giờ thì địa chỉ
 *     không đổi để bảng không nhấp nháy mỗi lần tải lại ngầm (đổi cửa sổ).
 * Dấu «vừa thay» giữ 24 giờ.
 */
export const THUMBNAIL_MARK_TTL_MS = 24 * 3_600_000;
const HOUR_MS = 3_600_000;

export function thumbnailVersion(
  changedAt: number | undefined,
  reloadedAt: number,
  fetchedAt: number,
): number {
  const hour = fetchedAt > 0 ? Math.floor(fetchedAt / HOUR_MS) * HOUR_MS : 0;
  return Math.max(changedAt ?? 0, reloadedAt > 0 ? reloadedAt : 0, hour);
}

export const THUMBNAIL_VERSION_PARAM = 'halo';

export function freshThumbnailUrl(url: string, version: number): string {
  if (!url || !version) return url;
  return `${url}${url.includes('?') ? '&' : '?'}${THUMBNAIL_VERSION_PARAM}=${version}`;
}

/** Bỏ dấu quá 24 giờ (ảnh đã đổi từ lâu — khỏi phá bộ đệm nữa). */
export function pruneThumbnailMarks(
  marks: Record<string, number>,
  now: number,
): Record<string, number> {
  return Object.fromEntries(
    Object.entries(marks).filter(([, at]) => now - at < THUMBNAIL_MARK_TTL_MS),
  );
}

/**
 * Nhãn tag của một buổi phát trực tiếp (27/10/2026): YouTube có lúc tự xoá tag sau khi app ghi; máy chủ nhớ bộ tag,
 * kiểm lại theo mốc và ghi lại (`tagsKeeper`). «25 tag» / «0/25 tag — YouTube xoá, app sẽ ghi lại» / «25 tag · đã ghi
 * lại 2 lần» / «0 tag · ghi lại hỏng: …».
 */
export function liveTagsLabel(broadcast: Pick<LiveBroadcastDto, 'tags' | 'tagsKeeper'>): string {
  const keeper = broadcast.tagsKeeper;
  const have = broadcast.tags.length;
  if (keeper?.error) return `${have} tag · ghi lại hỏng: ${keeper.error}`;
  if (keeper && !have && keeper.wanted.length) {
    return `0/${keeper.wanted.length} tag — YouTube xoá, app sẽ ghi lại`;
  }
  const base = have ? `${have} tag` : 'chưa có tag';
  return keeper?.rewrites ? `${base} · đã ghi lại ${keeper.rewrites} lần` : base;
}
